import { SunIcon } from "@heroicons/react/24/outline";
import Decimal from "decimal.js-light";

import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import { AbsenceTypeId } from "~/modules/absence/api/absence/absenceTypes.ts";
import { useStaffMembersVacationLedgerReport } from "~/modules/absence/api/vacationLedgerEntry/vacationLedgerEntryQueries.ts";
import { useAbsenceActions } from "~/modules/absence/hooks/useAbsenceActions.tsx";
import { AddPillButton } from "~/modules/dashboard/components/AddPillButton.tsx";
import { FigureRow } from "~/modules/dashboard/components/FigureRow.tsx";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

type Props = {
	staffMemberId: string;
};

function formatDays(days: Decimal): string {
	return `${formatNumberWithComma(days.toNumber(), 2)} ${days.equals(1) ? "Tag" : "Tage"}`;
}

// "Offen" is the figure of the absences page (`RemainingVacationDays`), which is not bound to a year.
// The box has no "Genommen" row because the report has no figure of the current year: it has only the
// all-time `daysTaken`, and the client wants no backend change for a year figure.
// The "+" opens the create sidebar only, so the hook gets no absence lists.
export function VacationBox({ staffMemberId }: Props) {
	const { data, isPending, isError } = useStaffMembersVacationLedgerReport(staffMemberId);
	const { dialogs, openCreate } = useAbsenceActions({ absences: [], absenceTypes: [], staffMemberId });
	const year = new Date().getFullYear();
	const daysRemaining = data ? new Decimal(data.daysRemaining) : null;

	return (
		<div>
			<div className="mb-3 flex min-h-7 items-center justify-between gap-2">
				<h3 className="text-lg font-semibold text-gray-900">{`Urlaub ${year}`}</h3>
				<AddPillButton label="Urlaub eintragen"
							   onClick={() => openCreate({ absenceTypeId: AbsenceTypeId.Vacation })} />
			</div>
			<div className="rounded-lg border border-gray-200 bg-white p-3">
				{isPending && <LoadingSpinner theme="neutral" />}
				{isError && <p className="text-sm text-gray-500">Der Urlaub konnte nicht geladen werden.</p>}
				{daysRemaining && (
					<dl>
						<FigureRow icon={SunIcon}
								   label="Offen"
								   value={formatDays(daysRemaining)}
								   valueClassName={daysRemaining.isNegative() ? "text-danger-500" : "text-gray-900"} />
					</dl>
				)}
			</div>
			{dialogs}
		</div>
	);
}
